<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { faInfo } from '@fortawesome/duotone-regular-svg-icons'
import arrayUtils from '~/utils/arrayUtils'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import useSlugify from '~/composables/useSlugify'
import useContentApi from '~/api/contentApi'
import Button from '~/components/ui/Button.vue'

const { truthy } = arrayUtils()
const { findContents } = useContentApi()
const { slugify } = useSlugify()

const { currentSubject } = storeToRefs(useSubjectsStore())
const { selectedGrade, selectedAddonLocationId, productHeaders } = storeToRefs(useFilterStore())
const { getRelatedLocationsByGradeAndSubject, getRelatedLocationsByLocationId } = useProductStore()

const locationIds = ref<number[]>([])

const showButton = computed(() => (productHeaders.value.colophons || []).length > 0)

const loadColophons = async () => {
  if (!locationIds.value.length)
    return
  try {
    productHeaders.value.colophons = (await findContents<BaseItem>({
      locationIdCriterion: locationIds.value,
      sortField: 'priority',
      sortOrder: 'asc'
    }))
  } catch (error) {
    productHeaders.value.colophons = []
    throw error
  }
}

watch([selectedGrade, currentSubject], async () => {
  if (!(selectedGrade.value && currentSubject.value))
    return
  const { colophonLocationIds } = await getRelatedLocationsByGradeAndSubject(selectedGrade.value, currentSubject.value)
  locationIds.value = [...colophonLocationIds].filter(truthy<number>)
  await loadColophons()
}, { immediate: true })

watch(selectedAddonLocationId, async () => {
  if (!selectedAddonLocationId.value)
    return
  const { colophonLocationIds } = await getRelatedLocationsByLocationId(selectedAddonLocationId.value)
  locationIds.value = [...colophonLocationIds].filter(truthy<number>)
  await loadColophons()
}, { immediate: true })
</script>

<template>
  <div v-if="showButton">
    <Button
      v-if="currentSubject && selectedGrade"
      shape="square"
      variant="outline"
      :to="{
        name: 'colophon_subject',
        params: {
          subject: currentSubject.toLowerCase(),
          grade: selectedGrade.toLowerCase(),
        },
      }"
    >
      <font-awesome-icon :icon="faInfo" />
    </Button>
    <Button
      v-if="selectedAddonLocationId && productHeaders.colophons"
      variant="outline"
      shape="square"
      :to="{
        name: 'colophon',
        params: {
          locationId: productHeaders.colophons[0].locationId,
          slug: slugify(productHeaders.colophons[0].title),
        },
      }"
    >
      <font-awesome-icon :icon="faInfo" />
    </Button>
  </div>
</template>
